import { Box, Text } from '@mantine/core';
import { IconPlayerPlay, IconBroadcast, IconListTree } from '@tabler/icons-react';
import type { ChildKind } from '../AppContentUtils';

interface ChildThumbnailProps {
  url: string | null;
  kind: ChildKind;
  title: string;
  /** Rendered width in px; height follows 16:9. */
  width?: number;
  onAir?: boolean;
  dimmed?: boolean;
}

const KIND_ICON = {
  series: IconListTree,
  video: IconPlayerPlay,
  live: IconBroadcast,
} as const;

/**
 * A 16:9 card the way Home renders a tile: the art when it exists, else a
 * quiet kind glyph on the surface tone. Lazy-loaded; a fixed box so rows never
 * reflow when an image lands.
 */
export function ChildThumbnail({ url, kind, title, width = 96, onAir, dimmed }: ChildThumbnailProps) {
  const height = Math.round((width * 9) / 16);
  const Icon = KIND_ICON[kind];
  return (
    <Box
      style={{
        position: 'relative',
        width,
        height,
        flex: '0 0 auto',
        borderRadius: 'var(--mantine-radius-sm)',
        overflow: 'hidden',
        backgroundColor: 'var(--mantine-color-gray-1)',
        opacity: dimmed ? 0.5 : 1,
      }}
    >
      {url ? (
        <img
          src={url}
          alt={title}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Box
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--mantine-color-gray-5)',
          }}
        >
          <Icon size={Math.max(16, Math.round(width / 5))} stroke={1.5} />
        </Box>
      )}
      {onAir && (
        <Text
          size="10px"
          fw={700}
          c="white"
          px={5}
          style={{
            position: 'absolute',
            top: 4,
            left: 4,
            lineHeight: '16px',
            borderRadius: 3,
            backgroundColor: 'var(--mantine-color-red-7)',
            letterSpacing: 0.5,
          }}
        >
          LIVE
        </Text>
      )}
    </Box>
  );
}
